import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api';
import L from 'leaflet';
import {
  Alert,
  Badge,
  Button,
  Collapse,
  Timeline,
  Descriptions,
  Divider,
  Empty,
  Input,
  Modal,
  Spin,
  Tag,
  Typography,
  Tooltip,
  Avatar,
} from 'antd';
import {
  CarOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  UserOutlined,
  ShoppingOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  AimOutlined,
  WifiOutlined,
  DisconnectOutlined,
  DashboardOutlined,
  SearchOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { disconnectTrackingSocket, getTrackingSocket } from '../../services/trackingSocket';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import truckImg from '../../assets/image/delivery-truck.png';
import LeafletDriversMap from '../../components/maps/LeafletDriversMap';
import { GOOGLE_MAPS_LIBRARIES } from '../../components/maps/GoogleCoverageMap';
import MainLayout from '../../components/MainLayout';

const { Title, Text } = Typography;

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const DEFAULT_CENTER = { lat: 23.0225, lng: 72.5714 };
const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
// Key is always loaded from DB (appConfig/public) — env var is intentionally ignored
// to avoid useJsApiLoader singleton conflicts when env and DB keys differ.
const ANIMATION_DURATION_MS = 800;

const MAP_OPTIONS = {
  fullscreenControl: false,
  streetViewControl: false,
  mapTypeControl: false,
};

const TRUCK_ICON = {
  path: 'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z',
  fillColor: '#1677ff',
  fillOpacity: 1,
  strokeColor: '#0050b3',
  strokeWeight: 1,
  scale: 1.4,
  anchor: { x: 12, y: 12 },
};



// ── Driver row card in the right panel ───────────────────────────────────────
const DriverRow = ({ driverId, driver, isSelected, onFocus, onViewOrder }) => {
  const lat = Number(driver?.lat);
  const lng = Number(driver?.lng);
  const hasGps = Number.isFinite(lat) && Number.isFinite(lng);
  // Backend now sends `orderId` as parent Order.id and `shipmentId` separately.
  const hasOrderId = driver?.orderId !== undefined && driver?.orderId !== null && String(driver.orderId).trim() !== '';
  const initials = (driver.driverName || 'D')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      onClick={() => { if (hasGps) onFocus(driverId, driver); }}
      style={{
        padding: '12px 14px',
        borderRadius: 10,
        cursor: hasGps ? 'pointer' : 'default',
        background: isSelected ? '#e6f4ff' : '#fff',
        border: `1.5px solid ${isSelected ? '#1677ff' : '#f0f0f0'}`,
        transition: 'all 0.2s',
        marginBottom: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar
          size={40}
          style={{
            background: hasGps ? '#1677ff' : '#bfbfbf',
            fontWeight: 700,
            fontSize: 14,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text strong style={{ fontSize: 13, color: '#1e293b' }} ellipsis>
              <UserOutlined style={{ marginRight: 5, color: '#1677ff' }} />
              {driver.driverName}
            </Text>
            <Badge
              status={hasGps ? 'processing' : 'warning'}
              text={
                <Text style={{ fontSize: 11, color: hasGps ? '#52c41a' : '#fa8c16' }}>
                  {hasGps ? 'Live' : 'Waiting'}
                </Text>
              }
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
            <ShoppingOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
            <Text style={{ fontSize: 12, color: '#595959' }}>
              Order #{driver.orderId}{driver?.shipmentId ? ` (Shipment ${driver.shipmentId})` : ''}
            </Text>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
            <EnvironmentOutlined style={{ fontSize: 11, color: hasGps ? '#52c41a' : '#d9d9d9' }} />
            {hasGps ? (
              <Text style={{ fontSize: 11, color: '#595959', fontFamily: 'monospace' }}>
                {lat.toFixed(4)}, {lng.toFixed(4)}
              </Text>
            ) : (
              <Text style={{ fontSize: 11, color: '#faad14' }}>Waiting for GPS…</Text>
            )}
          </div>

          {driver.updatedAt && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <ClockCircleOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
              <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
                {new Date(driver.updatedAt).toLocaleTimeString()}
              </Text>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <Tooltip title={hasOrderId ? 'View order details' : 'No order id'}>
            <Button
              type="text"
              size="small"
              disabled={!hasOrderId}
              icon={<EyeOutlined style={{ color: hasOrderId ? '#1677ff' : '#bfbfbf', fontSize: 16 }} />}
              style={{ padding: 4 }}
              onClick={(e) => {
                e.stopPropagation();
                if (hasOrderId) onViewOrder(String(driver.orderId));
              }}
            />
          </Tooltip>

          {hasGps && (
            <Tooltip title="Focus on map">
              <Button
                type="text"
                size="small"
                icon={<AimOutlined style={{ color: '#1677ff', fontSize: 16 }} />}
                style={{ padding: 4 }}
                onClick={(e) => { e.stopPropagation(); onFocus(driverId, driver); }}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Google map panel (key required) ───────────────────────────────────────────
const GoogleMapPanel = ({ driverEntries, markerPositions, drivers, selectedDriverId, setSelectedDriverId, onMapLoad }) => (
  <GoogleMap
    mapContainerStyle={MAP_CONTAINER_STYLE}
    center={DEFAULT_CENTER}
    zoom={12}
    onLoad={onMapLoad}
    options={MAP_OPTIONS}
  >
    {driverEntries.map(([driverId]) => {
      const pos = markerPositions[driverId];
      const driver = drivers[driverId];
      if (!pos || !driver) return null;
      const isSelected = selectedDriverId === driverId;
      return (
        <Marker
          key={driverId}
          position={pos}
          icon={{
            url: truckImg,
            scaledSize: new window.google.maps.Size(44, 44),
            anchor: new window.google.maps.Point(22, 22),
          }}
          title={driver.driverName}
          tracksViewChanges={false}
          onClick={() => setSelectedDriverId(isSelected ? null : driverId)}
        >
          {isSelected && (
            <InfoWindow position={pos} onCloseClick={() => setSelectedDriverId(null)}>
              <div style={{ minWidth: 150 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: '#1e293b' }}>
                  {driver.driverName}
                </div>
                <div style={{ fontSize: 12, color: '#555', marginBottom: 2 }}>
                  <ShoppingOutlined style={{ marginRight: 4 }} />Order #{driver.orderId}
                </div>
                {driver.updatedAt && (
                  <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {new Date(driver.updatedAt).toLocaleTimeString()}
                  </div>
                )}
              </div>
            </InfoWindow>
          )}
        </Marker>
      );
    })}
  </GoogleMap>
);

// ── Module-level Google Maps loader (avoids re-calling loader with different keys) ──
const GoogleMapsView = ({ apiKey, driverEntries, markerPositions, drivers, selectedDriverId, setSelectedDriverId, onMapLoad, authError, setAuthError }) => {
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey, libraries: GOOGLE_MAPS_LIBRARIES });
  const CENTERED = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  if (authError || loadError) {
    return (
      <div style={CENTERED}>
        <div style={{ padding: 32, maxWidth: 480, textAlign: 'center' }}>
          <ExclamationCircleOutlined style={{ fontSize: 40, color: '#ff4d4f', marginBottom: 12 }} />
          <Title level={5} style={{ color: '#ff4d4f' }}>Google Maps could not load</Title>
          <Alert
            type="warning"
            style={{ textAlign: 'left', marginBottom: 12 }}
            message="Fix in Google Cloud Console"
            description={
              <ol style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                <li>Enable <strong>Maps JavaScript API</strong></li>
                <li>Attach a billing method</li>
                <li>Add <code>http://localhost:*</code> to HTTP referrer restrictions</li>
              </ol>
            }
          />
          <Button icon={<ReloadOutlined />} onClick={() => { setAuthError(false); window.location.reload(); }}>
            Reload after fixing
          </Button>
        </div>
      </div>
    );
  }
  if (!isLoaded) {
    return <div style={CENTERED}><Spin tip="Loading map…" size="large" /></div>;
  }
  return (
    <GoogleMapPanel
      driverEntries={driverEntries}
      markerPositions={markerPositions}
      drivers={drivers}
      selectedDriverId={selectedDriverId}
      setSelectedDriverId={setSelectedDriverId}
      onMapLoad={onMapLoad}
    />
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const LiveTracking = () => {
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');
  const USE_GOOGLE_MAPS = !!googleMapsApiKey;
  const [drivers, setDrivers] = useState({});
  // markerPositions drives Marker position props — updated smoothly via RAF animation
  const [markerPositions, setMarkerPositions] = useState({});
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [orderDestCache, setOrderDestCache] = useState({});
  const [routeLatLngs, setRouteLatLngs] = useState(null);
  const [routeDistanceKm, setRouteDistanceKm] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [driverSearch, setDriverSearch] = useState('');
  const [debouncedDriverSearch, setDebouncedDriverSearch] = useState('');
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderModalLoading, setOrderModalLoading] = useState(false);
  const [orderModalError, setOrderModalError] = useState('');
  const [orderModalOrderId, setOrderModalOrderId] = useState('');
  const [orderModalData, setOrderModalData] = useState(null);

  const mapRef = useRef(null);
  const autoFocusedDrivers = useRef(new Set());
  const didInitialFit = useRef(false);
  const didAutoSelectFirstActive = useRef(false);
  // last GPS position received per driver — used as animation start point
  const prevGpsPos = useRef({});
  // active requestAnimationFrame ids per driver
  const animFrames = useRef({});

  const getValidLatLng = useCallback((d) => {
    const lat = Number(d?.lat);
    const lng = Number(d?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }, []);

  useEffect(() => {
    ApiServices('get', ApiConfig.APP_CONFIG_PUBLIC).then((res) => {
      const list = res?.data?.data || [];
      const keyRow = list.find((r) => r.Key === 'Google Maps API Key');
      const enabledRow = list.find((r) => r.Key === 'Google Maps Enabled');
      const enabled = enabledRow?.value === 'true';
      if (enabled && keyRow?.value) setGoogleMapsApiKey(keyRow.value);
      else setGoogleMapsApiKey('');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!USE_GOOGLE_MAPS) return undefined;
    window.gm_authFailure = () => setAuthError(true);
    return () => { delete window.gm_authFailure; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedDriverSearch(driverSearch.trim()), 200);
    return () => clearTimeout(t);
  }, [driverSearch]);

  const onGoogleMapLoad = useCallback((map) => { mapRef.current = map; }, []);
  const onLeafletMapCreated = useCallback((map) => { mapRef.current = map; }, []);

  const fitToDrivers = useCallback((driversMap) => {
    const map = mapRef.current;
    const valid = Object.values(driversMap).map(getValidLatLng).filter(Boolean);
    if (valid.length === 0) return;
    if (!map) return;

    if (USE_GOOGLE_MAPS) {
      if (!window.google) return;
      if (valid.length === 1) {
        map.panTo({ lat: valid[0].lat, lng: valid[0].lng });
        map.setZoom(15);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        valid.forEach((d) => bounds.extend({ lat: d.lat, lng: d.lng }));
        map.fitBounds(bounds, 60);
      }
      return;
    }

    // Leaflet fit (no API key)
    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 15, { animate: true });
    } else {
      const bounds = L.latLngBounds(valid.map((d) => [d.lat, d.lng]));
      map.fitBounds(bounds, { padding: [60, 60] });
    }
  }, [getValidLatLng]);

  const fitToMarkerPositions = useCallback((positions) => {
    const map = mapRef.current;
    if (!map || !positions) return;
    const valid = Object.values(positions)
      .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (!valid.length) return;

    if (USE_GOOGLE_MAPS) {
      if (!window.google) return;
      if (valid.length === 1) {
        map.panTo({ lat: valid[0].lat, lng: valid[0].lng });
        map.setZoom(15);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        valid.forEach((d) => bounds.extend({ lat: d.lat, lng: d.lng }));
        map.fitBounds(bounds, 60);
      }
      return;
    }

    // Leaflet fit
    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 15, { animate: true });
    } else {
      const bounds = L.latLngBounds(valid.map((d) => [d.lat, d.lng]));
      map.fitBounds(bounds, { padding: [60, 60] });
    }
  }, []);

  // Initial zoom: once we have at least one GPS marker, fit to it.
  useEffect(() => {
    if (didInitialFit.current) return;
    const keys = Object.keys(markerPositions || {});
    if (keys.length === 0) return;
    didInitialFit.current = true;
    // Prefer markerPositions because drivers snapshot may have string/nullable coords.
    setTimeout(() => fitToMarkerPositions(markerPositions), 0);
  }, [markerPositions, fitToMarkerPositions]);

  // Animate marker from (fromLat, fromLng) → (toLat, toLng) over ANIMATION_DURATION_MS
  const animateMarker = useCallback((driverId, fromLat, fromLng, toLat, toLng) => {
    if (animFrames.current[driverId]) cancelAnimationFrame(animFrames.current[driverId]);

    const start = performance.now();

    const step = (now) => {
      const t = Math.min((now - start) / ANIMATION_DURATION_MS, 1);
      // ease-out cubic — fast start, gentle landing
      const ease = 1 - Math.pow(1 - t, 3);
      const lat = fromLat + (toLat - fromLat) * ease;
      const lng = fromLng + (toLng - fromLng) * ease;

      setMarkerPositions((prev) => ({ ...prev, [driverId]: { lat, lng } }));

      if (t < 1) {
        animFrames.current[driverId] = requestAnimationFrame(step);
      } else {
        delete animFrames.current[driverId];
      }
    };

    animFrames.current[driverId] = requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    const socket = getTrackingSocket(SOCKET_URL);

    const onConnect = () => { setConnected(true); socket.emit('joinAdminTracking'); };
    const onDisconnect = () => setConnected(false);

    const onActiveDrivers = (snapshot) => {
      setDrivers({ ...snapshot });
      // Snap markers to initial positions for drivers that already have GPS
      const initPos = {};
      Object.entries(snapshot).forEach(([driverId, driver]) => {
        if (driver.lat && driver.lng) {
          initPos[driverId] = { lat: driver.lat, lng: driver.lng };
          prevGpsPos.current[driverId] = { lat: driver.lat, lng: driver.lng };
        }
      });
      setMarkerPositions(initPos);
      // Initial focus: fit to markers with GPS if present.
      if (!didInitialFit.current) {
        setTimeout(() => fitToMarkerPositions(initPos), 0);
      }
      setTimeout(() => fitToDrivers(snapshot), 300);
    };

    const onDriverConnected = ({ driverId, orderId, driverName }) =>
      setDrivers((prev) => ({
        ...prev,
        [driverId]: { ...(prev[driverId] || {}), orderId, driverName, lat: null, lng: null },
      }));

    const onLocationUpdate = ({ driverId, orderId, driverName, lat, lng, updatedAt }) => {
      // Update sidebar data
      setDrivers((prev) => {
        const ex = prev[driverId];
        if (ex && ex.lat === lat && ex.lng === lng) return prev;
        const next = { ...prev, [driverId]: { lat, lng, orderId, driverName, updatedAt } };
        if (!ex?.lat && !autoFocusedDrivers.current.has(driverId)) {
          autoFocusedDrivers.current.add(driverId);
          setTimeout(() => fitToDrivers(next), 0);
        }
        return next;
      });

      // Auto-focus the first active (GPS) driver once, when the first GPS fix arrives.
      // This matches "first active driver" expectation on page open.
      if (!didAutoSelectFirstActive.current) {
        const ll = getValidLatLng({ lat, lng });
        if (ll) {
          didAutoSelectFirstActive.current = true;
          focusDriver(String(driverId), { lat, lng, orderId, driverName, updatedAt });
        }
      }

      // Animate or snap the marker
      const prev = prevGpsPos.current[driverId];
      if (prev && (prev.lat !== lat || prev.lng !== lng)) {
        animateMarker(driverId, prev.lat, prev.lng, lat, lng);
      } else if (!prev) {
        // First GPS fix — snap immediately, no animation
        setMarkerPositions((mp) => ({ ...mp, [driverId]: { lat, lng } }));
      }
      prevGpsPos.current[driverId] = { lat, lng };
    };

    const onDriverDisconnected = ({ driverId }) => {
      autoFocusedDrivers.current.delete(driverId);
      delete prevGpsPos.current[driverId];
      if (animFrames.current[driverId]) {
        cancelAnimationFrame(animFrames.current[driverId]);
        delete animFrames.current[driverId];
      }
      setMarkerPositions((mp) => { const n = { ...mp }; delete n[driverId]; return n; });
      setDrivers((prev) => { const n = { ...prev }; delete n[driverId]; return n; });
      setSelectedDriverId((cur) => (cur === driverId ? null : cur));
    };

    if (socket.connected) onConnect();
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('activeDrivers', onActiveDrivers);
    socket.on('driverConnected', onDriverConnected);
    socket.on('locationUpdate', onLocationUpdate);
    socket.on('driverDisconnected', onDriverDisconnected);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('activeDrivers', onActiveDrivers);
      socket.off('driverConnected', onDriverConnected);
      socket.off('locationUpdate', onLocationUpdate);
      socket.off('driverDisconnected', onDriverDisconnected);
      // Cancel all in-flight animations
      Object.values(animFrames.current).forEach(cancelAnimationFrame);
      disconnectTrackingSocket();
    };
  }, [animateMarker, fitToDrivers]);

  // Ensure "select driver from panel" focuses even if map is created slightly later
  useEffect(() => {
    if (!selectedDriverId || !mapRef.current) return;
    const key = String(selectedDriverId);
    const d = drivers?.[key] || drivers?.[selectedDriverId];
    const fromMarker = markerPositions?.[key] || markerPositions?.[selectedDriverId];
    const ll = getValidLatLng(fromMarker || d);
    if (!ll) return;

    if (USE_GOOGLE_MAPS) {
      mapRef.current.panTo({ lat: ll.lat, lng: ll.lng });
      mapRef.current.setZoom(15);
      return;
    }

    try { mapRef.current.invalidateSize?.(); } catch (_) { /* noop */ }
    requestAnimationFrame(() => {
      try { mapRef.current.invalidateSize?.(); } catch (_) { /* noop */ }
      mapRef.current.setView([ll.lat, ll.lng], 15, { animate: true });
    });
  }, [selectedDriverId, drivers, markerPositions, getValidLatLng]);

  const driverEntries = Object.entries(drivers);
  const activeCount = driverEntries.filter(([, d]) => !!getValidLatLng(d)).length;

  const normalizedQuery = debouncedDriverSearch.toLowerCase();
  const filteredDriverEntries = normalizedQuery
    ? driverEntries.filter(([driverId, d]) => {
      const haystack = [
        driverId,
        d?.driverName,
        d?.orderId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    })
    : driverEntries;

  const focusDriver = (driverId, driver) => {
    const map = mapRef.current;
    const key = String(driverId);
    // Always set selection even if map isn't ready yet; effect above will focus later.
    setSelectedDriverId(key);
    if (!map) return;

    const pos =
      markerPositions?.[key] ||
      markerPositions?.[driverId] ||
      (driver ? { lat: driver.lat, lng: driver.lng } : null);

    const ll = getValidLatLng(pos);
    if (!ll) return;

    if (USE_GOOGLE_MAPS) {
      map.panTo({ lat: ll.lat, lng: ll.lng });
      map.setZoom(15);
      return;
    }

    // Leaflet: ensure the map is sized, then focus next frame.
    // (Prevents "no pan" glitches when container/layout just changed.)
    try { map.invalidateSize?.(); } catch (_) { /* noop */ }
    requestAnimationFrame(() => {
      try { map.invalidateSize?.(); } catch (_) { /* noop */ }
      map.setView([ll.lat, ll.lng], 15, { animate: true });
    });
  };

  const openOrderDetailsModal = useCallback(async (orderId) => {
    setOrderModalOpen(true);
    setOrderModalOrderId(orderId);
    setOrderModalData(null);
    setOrderModalError('');
    setOrderModalLoading(true);
    try {
      const res = await ApiServices('get', `${ApiConfig.ORDERS}/${orderId}`);
      if (res?.success && res?.data) {
        setOrderModalData(res.data);
      } else {
        setOrderModalError(res?.message || 'Failed to load order details');
      }
    } catch (e) {
      setOrderModalError(e?.message || 'Failed to load order details');
    } finally {
      setOrderModalLoading(false);
    }
  }, []);

  const formatDateTime = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toLocaleString();
  };

  const getOrderItems = (order) => order?.orderItems || order?.OrderItems || [];
  const getOrderAddresses = (order) => order?.orderAddresses || order?.OrderAddresses || [];
  const getShipments = (order) => order?.shipments || order?.Shipments || [];
  const getDeliveryAddress = (order) => {
    const list = getOrderAddresses(order);
    return list.find((a) => a?.addressType === 'Delivery') || null;
  };

  const getAddressLatLng = (addr) => {
    if (!addr) return null;
    const lat = Number(
      addr?.lat ?? addr?.latitude ?? addr?.deliveryLat ?? addr?.delivery_lat ?? addr?.address_lat ?? addr?.addressLat
    );
    const lng = Number(
      addr?.lng ?? addr?.longitude ?? addr?.deliveryLng ?? addr?.delivery_lng ?? addr?.address_lng ?? addr?.addressLng
    );
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  };

  const haversineKm = (a, b) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const sa = Math.sin(dLat / 2) ** 2
      + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(sa));
  };

  // Selected driver route: current GPS → customer delivery coords
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const key = selectedDriverId ? String(selectedDriverId) : '';
      if (!key) {
        setRouteLatLngs(null);
        setRouteDistanceKm(null);
        setRouteLoading(false);
        return;
      }

      const d = drivers?.[key] || drivers?.[selectedDriverId];
      const orderId = d?.orderId ? String(d.orderId) : '';
      const fromPos = markerPositions?.[key] || markerPositions?.[selectedDriverId];
      const from = getValidLatLng(fromPos || d);

      if (!orderId || !from) {
        setRouteLatLngs(null);
        setRouteDistanceKm(null);
        setRouteLoading(false);
        return;
      }

      let dest = orderDestCache?.[orderId] || null;

      // Fetch order once to get delivery coords (cached by orderId)
      if (!dest) {
        setRouteLoading(true);
        try {
          const res = await ApiServices('get', `${ApiConfig.ORDERS}/${orderId}`);
          const order = res?.data || null;
          const addr = getDeliveryAddress(order);
          const ll = getAddressLatLng(addr);
          if (ll && !cancelled) {
            dest = ll;
            setOrderDestCache((prev) => ({ ...prev, [orderId]: ll }));
          }
        } catch (_) {
          // noop (fallback to no route)
        } finally {
          if (!cancelled) setRouteLoading(false);
        }
      }

      if (!dest) {
        setRouteLatLngs(null);
        setRouteDistanceKm(null);
        return;
      }

      // OSRM public route (no API key). Fallback to straight line.
      setRouteLoading(true);
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${dest.lng},${dest.lat}?overview=full&geometries=geojson`;
        const r = await fetch(url);
        const j = await r.json();
        const coords = j?.routes?.[0]?.geometry?.coordinates;
        const distM = j?.routes?.[0]?.distance;

        if (!cancelled && Array.isArray(coords) && coords.length >= 2) {
          // OSRM returns [lng, lat]
          setRouteLatLngs(coords.map(([lng, lat]) => [lat, lng]));
          setRouteDistanceKm(Number.isFinite(Number(distM)) ? Number(distM) / 1000 : haversineKm(from, dest));
        } else if (!cancelled) {
          setRouteLatLngs([[from.lat, from.lng], [dest.lat, dest.lng]]);
          setRouteDistanceKm(haversineKm(from, dest));
        }
      } catch (_) {
        if (!cancelled) {
          setRouteLatLngs([[from.lat, from.lng], [dest.lat, dest.lng]]);
          setRouteDistanceKm(haversineKm(from, dest));
        }
      } finally {
        if (!cancelled) setRouteLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [selectedDriverId, drivers, markerPositions, orderDestCache, getValidLatLng]);

  const buildTimelineItems = (order) => {
    const shipments = getShipments(order);
    const s0 = shipments[0] || {};
    const status = order?.orderStatus ?? order?.status ?? '—';
    const shipmentStatus = s0?.shipmentStatus ?? s0?.status ?? null;
    const row = (label, ts, fallbackText) => ({
      label,
      ts,
      text: ts ? formatDateTime(ts) : (fallbackText || '—'),
    });

    return [
      row('Order Created', order?.createdAt, null),
      row('Picked Up', s0?.pickedUpAt, shipmentStatus === 'PICKED_UP' ? 'Picked up' : null),
      row('Out for Delivery', s0?.outForDeliveryAt, shipmentStatus === 'OUT_FOR_DELIVERY' ? 'Out for delivery' : null),
      row('Delivered', s0?.deliveredAt, (status === 'DELIVERED' || status === 'COMPLETED') ? 'Delivered' : null),
    ];
  };

  const CENTERED = {
    position: 'absolute', inset: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  const renderMapContent = () => {
    if (!USE_GOOGLE_MAPS) {
      const key = selectedDriverId ? String(selectedDriverId) : '';
      const d = key ? (drivers?.[key] || drivers?.[selectedDriverId]) : null;
      const orderId = d?.orderId ? String(d.orderId) : '';
      const dest = orderId ? orderDestCache?.[orderId] : null;

      return (
        <LeafletDriversMap
          center={DEFAULT_CENTER}
          zoom={12}
          driverEntries={filteredDriverEntries}
          markerPositions={markerPositions}
          selectedId={selectedDriverId}
          onSelect={setSelectedDriverId}
          truckIconUrl={truckImg}
          onMapCreated={onLeafletMapCreated}
          routeLatLngs={routeLatLngs}
          routeDest={dest}
          renderPopup={(driverId) => {
            const driver = drivers[driverId];
            if (!driver) return null;
            return (
              <div style={{ minWidth: 150 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: '#1e293b' }}>
                  {driver.driverName}
                </div>
                <div style={{ fontSize: 12, color: '#555', marginBottom: 2 }}>
                  Order #{driver.orderId}
                </div>
                {driver.updatedAt && (
                  <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                    {new Date(driver.updatedAt).toLocaleTimeString()}
                  </div>
                )}
              </div>
            );
          }}
        />
      );
    }

    return (
      <GoogleMapsView
        apiKey={googleMapsApiKey}
        driverEntries={filteredDriverEntries}
        markerPositions={markerPositions}
        drivers={drivers}
        selectedDriverId={selectedDriverId}
        setSelectedDriverId={setSelectedDriverId}
        onMapLoad={onGoogleMapLoad}
        authError={authError}
        setAuthError={setAuthError}
      />
    );
  };

  return (
    <MainLayout>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#fff', borderRadius: 10, padding: '10px 16px',
        border: '1px solid #f0f0f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'linear-gradient(135deg,#1677ff,#0050b3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CarOutlined style={{ color: '#fff', fontSize: 18 }} />
          </div>
          <div>
            <Title level={5} style={{ margin: 0, lineHeight: 1.2 }}>Live Driver Tracking</Title>
            <Text style={{ fontSize: 11, color: '#8c8c8c' }}>Real-time GPS updates every 4s</Text>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1677ff', lineHeight: 1 }}>{activeCount}</div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>On Road</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#595959', lineHeight: 1 }}>{driverEntries.length}</div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>Total</div>
            </div>
          </div>

          <Divider type="vertical" style={{ height: 32 }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {connected
              ? <WifiOutlined style={{ color: '#52c41a', fontSize: 16 }} />
              : <DisconnectOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />}
            <Text style={{ fontSize: 12, color: connected ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
              {connected ? 'Connected' : 'Disconnected'}
            </Text>
          </div>
        </div>
      </div>

      {/* ── Body: map + driver panel ── */}
      <div style={{ flex: 1, display: 'flex', gap: 12, minHeight: 0 }}>

        <div style={{
          flex: 1, borderRadius: 10, overflow: 'hidden',
          border: '1px solid #f0f0f0', minHeight: 0,
          position: 'relative',
          background: '#fafafa', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          {renderMapContent()}
          {selectedDriverId && (routeLoading || routeDistanceKm !== null) && (
            <div style={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              background: 'rgba(255,255,255,0.95)',
              border: '1px solid #f0f0f0',
              borderRadius: 999,
              padding: '6px 10px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              zIndex: 500,
              maxWidth: 'calc(100% - 24px)',
            }}>
              <EnvironmentOutlined style={{ color: '#1677ff' }} />
              <Text style={{ fontSize: 12, color: '#1e293b' }}>
                {routeLoading ? 'Building route…' : `Customer is ~${Number(routeDistanceKm || 0).toFixed(1)} km away`}
              </Text>
            </div>
          )}
        </div>

        {/* Driver panel */}
        <div style={{
          width: 300, display: 'flex', flexDirection: 'column',
          background: '#fff', borderRadius: 10,
          border: '1px solid #f0f0f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '12px 14px 10px',
            borderBottom: '1px solid #f0f0f0',
            background: '#fafafa',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <DashboardOutlined style={{ color: '#1677ff', fontSize: 15 }} />
                <Text strong style={{ fontSize: 13 }}>Active Drivers</Text>
              </div>
              <div style={{
                background: activeCount > 0 ? '#e6f4ff' : '#f5f5f5',
                color: activeCount > 0 ? '#1677ff' : '#8c8c8c',
                fontSize: 11, fontWeight: 700,
                padding: '1px 8px', borderRadius: 10,
              }}>
                {filteredDriverEntries.length}{filteredDriverEntries.length !== driverEntries.length ? ` / ${driverEntries.length}` : ''}
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <Input
                value={driverSearch}
                onChange={(e) => setDriverSearch(e.target.value)}
                allowClear
                size="small"
                placeholder="Search drivers, order id, driver id…"
                prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
              />
            </div>

            {debouncedDriverSearch && (
              <div style={{ marginTop: 6 }}>
                <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
                  Showing {filteredDriverEntries.length} result{filteredDriverEntries.length === 1 ? '' : 's'}
                </Text>
              </div>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px 6px' }}>
            {filteredDriverEntries.length > 0 ? (
              filteredDriverEntries.map(([driverId, driver]) => (
                <DriverRow
                  key={driverId}
                  driverId={driverId}
                  driver={driver}
                  isSelected={String(selectedDriverId) === String(driverId)}
                  onFocus={focusDriver}
                  onViewOrder={openOrderDetailsModal}
                />
              ))
            ) : (
              <div style={{ padding: '32px 8px', textAlign: 'center' }}>
                <CarOutlined style={{ fontSize: 32, color: '#d9d9d9', marginBottom: 10, display: 'block' }} />
                <Text style={{ fontSize: 12, color: '#bfbfbf', lineHeight: 1.6 }}>
                  {driverEntries.length > 0
                    ? <>No matches.<br />Try a different search.</>
                    : <>No active deliveries.<br />Drivers appear here once they pick up an order.</>}
                </Text>
              </div>
            )}
          </div>

          {driverEntries.length > 0 && (
            <div style={{
              padding: '8px 14px',
              borderTop: '1px solid #f0f0f0',
              background: '#fafafa',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <Badge status="processing" />
              <Text style={{ fontSize: 11, color: '#8c8c8c' }}>Live GPS • updates every 4s</Text>
            </div>
          )}
        </div>
      </div>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <EyeOutlined style={{ color: '#1677ff' }} />
              <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Order Details</span>
              {orderModalOrderId && (
                <Tag color="blue" style={{ marginInlineStart: 6 }}>{orderModalOrderId}</Tag>
              )}
            </div>
          </div>
        }
        open={orderModalOpen}
        onCancel={() => setOrderModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setOrderModalOpen(false)}>Close</Button>,
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            loading={orderModalLoading}
            onClick={() => openOrderDetailsModal(orderModalOrderId)}
            disabled={!orderModalOrderId}
          >
            Refresh
          </Button>,
        ]}
        width={820}
        destroyOnHidden
      >
        {orderModalLoading ? (
          <div style={{ padding: '24px 0', display: 'flex', justifyContent: 'center' }}>
            <Spin />
          </div>
        ) : orderModalError ? (
          <Alert type="error" showIcon message={orderModalError} />
        ) : orderModalData ? (
          <div>
            <Descriptions
              bordered
              size="small"
              column={2}
              style={{ marginBottom: 12 }}
            >
              <Descriptions.Item label="Order #">
                {orderModalData.orderId ?? orderModalData.id ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {String(orderModalData.orderStatus ?? orderModalData.status ?? '—')}
              </Descriptions.Item>
              <Descriptions.Item label="Customer">
                {orderModalData.customerName ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {orderModalData.customerPhone ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Payment">
                {orderModalData.paymentType ? (
                  <Tag color={orderModalData.paymentType === 'COD' ? 'orange' : 'green'}>
                    {orderModalData.paymentType}
                  </Tag>
                ) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                {orderModalData.amount !== undefined
                  ? `${orderModalData.currency_symbol || '₹'}${Number(orderModalData.amount || 0).toFixed(2)}`
                  : '—'}
              </Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '12px 0' }} />

            {/* Order Items */}
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Order Items</Text>
              {getOrderItems(orderModalData).length > 0 ? (
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 10, overflow: 'hidden' }}>
                  {getOrderItems(orderModalData).map((it, idx) => {
                    const name = it?.productName || it?.Product_name || it?.title || 'Item';
                    const qty = it?.quantity ?? 1;
                    const sym = it?.currency_symbol || orderModalData.currency_symbol || '₹';
                    const unit = Number(it?.productPrice ?? it?.Product_price ?? 0);
                    const total = Number(it?.Product_amount ?? it?.productAmount ?? (unit * qty) ?? 0);
                    return (
                      <div
                        key={it?.id || idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          borderTop: idx === 0 ? 'none' : '1px solid #f0f0f0',
                          background: '#fff',
                          gap: 12,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {name}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            Qty: {qty}
                            {unit ? ` • Unit: ${sym}${unit.toFixed(2)}` : ''}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1677ff', flexShrink: 0 }}>
                          {sym}{Number.isFinite(total) ? total.toFixed(2) : '0.00'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No items" />
              )}
            </div>

            {/* Route / Timeline */}
            <div style={{ marginBottom: 6 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Delivery Route / Timeline</Text>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 280, border: '1px solid #f0f0f0', borderRadius: 10, padding: 12 }}>
                  <Timeline
                    items={buildTimelineItems(orderModalData).map((t) => ({
                      children: (
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{t.label}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{t.text}</div>
                        </div>
                      ),
                    }))}
                  />
                </div>

                <div style={{ flex: 1, minWidth: 280, border: '1px solid #f0f0f0', borderRadius: 10, padding: 12 }}>
                  <Text strong style={{ fontSize: 13 }}>Delivery Address</Text>
                  <Divider style={{ margin: '8px 0' }} />
                  {getDeliveryAddress(orderModalData) ? (
                    <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6 }}>
                      {[
                        getDeliveryAddress(orderModalData)?.full_name || orderModalData.customerName,
                        getDeliveryAddress(orderModalData)?.line1,
                        getDeliveryAddress(orderModalData)?.line2,
                        getDeliveryAddress(orderModalData)?.landmark,
                        getDeliveryAddress(orderModalData)?.area,
                        getDeliveryAddress(orderModalData)?.building_name,
                        [getDeliveryAddress(orderModalData)?.city, getDeliveryAddress(orderModalData)?.state, getDeliveryAddress(orderModalData)?.zipcode].filter(Boolean).join(' '),
                        getDeliveryAddress(orderModalData)?.country,
                        getDeliveryAddress(orderModalData)?.phone || orderModalData.customerPhone,
                      ].filter(Boolean).join(', ')}
                    </div>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>Not available</Text>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Empty description="No data" />
        )}
      </Modal>
      </div>
    </MainLayout>
  );
};

export default LiveTracking;
